import * as React from 'react'

import { useQuery } from '@apollo/client/react'

import { useOrganizationId } from '~publish/contexts/AccountContext'

import { MentionCountsQuery } from './useChannelMentionCounts'

/**
 * Per-channel cap for the window count. High enough that the displayed number
 * is the real one for any plausible inbox; past it the bar shows `+1000`.
 */
export const RECENT_MENTIONS_LIMIT = 1_000

type UseRecentMentionCountProps = {
  /** Channels to count. Callers pass only mention-supported channels. */
  channels: { id: string }[]
  /** Size of the window, counting back from now. */
  days?: number
  skip?: boolean
}

type UseRecentMentionCountReturn = {
  /** Mentions received in the window, summed across the channels. */
  count: number | null
  /**
   * A channel hit the per-channel cap, so the sum is a floor rather than the
   * real total and should be shown as `+{count}`.
   */
  isCapped: boolean
  loading: boolean
  error: boolean
}

/**
 * Counts the mentions these channels received over the last `days`, in any
 * status. Drives the stats bar on the caught-up empty state.
 */
export function useRecentMentionCount({
  channels,
  days = 7,
  skip = false,
}: UseRecentMentionCountProps): UseRecentMentionCountReturn {
  const organizationId = useOrganizationId()

  const channelIds = React.useMemo(
    () => channels.map((channel) => channel.id),
    [channels],
  )

  // Sorted+joined key keeps the Apollo variables (and cache entry) stable when
  // the channels array identity changes but its contents don't.
  const channelIdsKey = [...channelIds].toSorted().join(',')

  // Anchored to the day so the window doesn't shift on every render, which
  // would make each render a fresh Apollo cache entry. Today counts as one of
  // the days, so seven days reaches back six.
  const publishedAt = React.useMemo(() => {
    const end = new Date()
    end.setHours(23, 59, 59, 999)
    const start = new Date(end)
    start.setDate(start.getDate() - (days - 1))
    start.setHours(0, 0, 0, 0)
    return { start: start.toISOString(), end: end.toISOString() }
  }, [days])

  const variables = React.useMemo(
    () => ({
      input: {
        organizationId: organizationId ?? '',
        engagementType: 'mention' as const,
        channelIds,
        filters: { publishedAt },
        limit: RECENT_MENTIONS_LIMIT,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organizationId, channelIdsKey, publishedAt],
  )

  // Skipping puts the query on Apollo's `standby` policy, which stops it
  // fetching but leaves the last result in place. Derived values have to be
  // gated on the same condition or they outlive the channels they came from.
  const shouldSkip = skip || !organizationId || channelIds.length === 0

  const { data, loading, error } = useQuery(MentionCountsQuery, {
    variables,
    fetchPolicy: 'cache-and-network',
    skip: shouldSkip,
  })

  return React.useMemo<UseRecentMentionCountReturn>(() => {
    const counts = data?.engagementCountsByChannelIds.counts

    if (shouldSkip || error || !counts) {
      return { count: null, isCapped: false, loading, error: Boolean(error) }
    }

    return {
      count: counts.reduce((total, channel) => total + channel.count, 0),
      // The cap applies per channel, so the sum understates the total as soon
      // as any single channel is truncated.
      isCapped: counts.some((channel) => channel.count >= channel.limit),
      loading,
      error: false,
    }
  }, [data, loading, error, shouldSkip])
}
